import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { ThemeToggle } from "./ThemeToggle";
import LanguageSelector from "./LanguageSelector";
import {
  FolderOpen,
  Users,
  Activity,
  LogOut,
  Settings,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";

// Sidebar Navigation Component
export const AdminSidebar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const isSuperAdmin = user?.role === "superadmin";

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: "/admin", icon: FolderOpen, label: t("nav.projects", "Projekte"), show: true },
    { path: "/admin/users", icon: Users, label: t("nav.users", "Benutzer"), show: isSuperAdmin },
    { path: "/admin/activity", icon: Activity, label: t("nav.activity", "Aktivitäten"), show: isSuperAdmin },
    { path: "/admin/settings", icon: Settings, label: t("nav.settings", "Einstellungen"), show: isSuperAdmin },
  ];

  return (
    <aside className="fixed left-0 top-0 h-full w-56 bg-[#1e3a5f] dark:bg-slate-900 text-white flex flex-col z-40">
      {/* Logo Section */}
      <div className="p-4 border-b border-white/10">
        <img src={LOGO_URL} alt="plutec" className="h-8 brightness-0 invert" />
        <p className="text-xs text-white/60 mt-1">comXline Admin</p>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {navItems.filter(item => item.show).map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.path} to={item.path}>
              <Button 
                variant="ghost" 
                className={`w-full justify-start text-white/80 hover:text-white hover:bg-white/10 ${
                  isActive(item.path) ? "bg-white/20 text-white" : ""
                }`}
              >
                <Icon className="w-4 h-4 mr-3" />
                {item.label}
              </Button>
            </Link>
          );
        })}
      </nav>
      
      {/* Footer */}
      <div className="p-3 border-t border-white/10 text-xs text-white/40 text-center">
        © 2026 plutec GmbH
      </div>
    </aside>
  );
};

// Top Header Component
export const AdminHeader = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  return (
    <header className="app-header ml-56">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo + Title */}
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="plutec" className="h-8" />
            <span className="font-manrope font-bold text-lg text-[#1e3a5f] dark:text-blue-300">
              comXline Admin
            </span>
          </div>
          
          {/* Right: Language, Theme, User, Logout */}
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <ThemeToggle />
            <span className="text-sm text-muted-foreground hidden lg:block">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t("common.logout", "Abmelden")}</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
